// src/shell/AppShell.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { API_BASE_URL } from '../services/api/config.js';
import { setFavicon, clearFavicon } from '../utils/setFavicon.js';
import Header from './Header.jsx';
import Sidebar from './Sidebar.jsx';

const BG_LIGHT = 'linear-gradient(175deg, #f5f5f7 0%, #ffffff 40%, #f5f5f7 100%)';

export default function AppShell() {
  const { phase, activeTenant } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const toggleSidebar = useCallback(() => setSidebarOpen(v => !v), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  // Close sidebar on navigation (mobile)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Set tab favicon from the active tenant; clear on unmount/logout.
  // The endpoint is public — browser fetches directly, no auth header needed.
  useEffect(() => {
    const faviconUrl = activeTenant?.favicon_url;
    if (!faviconUrl) return;
    setFavicon(`${API_BASE_URL}${faviconUrl}`);
    return () => clearFavicon();
  }, [activeTenant?.favicon_url]);

  if (phase === 'booting')     return null;
  if (phase === 'login')       return <Navigate to="/login" replace />;
  if (phase === 'no_tenants')  return <Navigate to="/no-access" replace />;
  if (phase === 'tenant_pick') return <Navigate to="/select-tenant" replace />;

  return (
    <>
      <style>{`
        .app-shell { display: flex; flex-direction: column; height: 100vh; background: ${BG_LIGHT}; }
        .app-body  { display: flex; flex: 1; overflow: hidden; position: relative; }
        .app-main  { flex: 1; overflow: auto; }

        .hamburger-btn {
          display: none;
          background: none;
          border: 1px solid rgba(0,0,0,0.1);
          border-radius: 6px;
          padding: 6px 8px;
          cursor: pointer;
          color: #3a3a3c;
          font-size: 18px;
          line-height: 1;
        }

        .sidebar-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.4);
          z-index: 99;
        }

        @media (max-width: 768px) {
          .hamburger-btn { display: block; }
          .sidebar-overlay.open { display: block; }
        }
      `}</style>
      <div className="app-shell">
        <Header hamburgerBtn={
          <button className="hamburger-btn" onClick={toggleSidebar} aria-label="Menu">
            {sidebarOpen ? '✕' : '☰'}
          </button>
        } />
        <div className="app-body">
          <div className={`sidebar-overlay${sidebarOpen ? ' open' : ''}`} onClick={closeSidebar} />
          <Sidebar open={sidebarOpen} />
          <main className="app-main">
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}
